import type { Err } from './common';
import type { BundleKey } from './types';

/*
 * Small ports of the shared ContentValidator helpers the Step 2 partials use, with the C# wording. The C# messages carry no location,
 * so each port picks the section, entry index and dotted path that the forms show the issue next to.
 */

/** C# `float.ToString()` for the bounds in a message (`0.1`, `1.5`, `30`). */
export const fmtNum = (v: number) => String(Math.round(v * 1e6) / 1e6);

/** `ContentValidator.Range`: "{where} needs {field} in {min}..{max}." (NaN fails, like the C# `!(v >= min && v <= max)`). */
export function rangeMsg(
  v: number,
  min: number,
  max: number,
  where: string,
  field: string,
): string | null {
  return v >= min && v <= max ? null : `${where} needs ${field} in ${fmtNum(min)}..${fmtNum(max)}.`;
}

/** `ContentValidator.IsChance`. */
export const isChance = (v: number) => v >= 0 && v <= 1;

/**
 * `ContentValidator.Section(list, key)`: an explicit null is "Bundle is missing '{key}'.", null entries are "Bundle has an empty entry
 * in '{key}'."; absent = empty (the server's parser gives the C# default). Returns the entries with their index.
 */
export function sectionList<T>(
  list: (T | null)[] | null | undefined,
  key: string,
  err: Err,
  section: BundleKey,
  base?: string,
): [T, number][] {
  const out: [T, number][] = [];
  if (list === null) {
    err(section, undefined, base ?? '', `Bundle is missing '${key}'.`);
    return out;
  }
  (list ?? []).forEach((x, i) => {
    if (x == null)
      err(
        section,
        base === undefined ? i : undefined,
        base === undefined ? '' : `${base}.${i}`,
        `Bundle has an empty entry in '${key}'.`,
      );
    else out.push([x, i]);
  });
  return out;
}

/** `ContentValidator.Ids` over already-sectioned entries; `base` puts the issue at `base.i.id` in an object section. */
export function idsOf<T>(
  entries: [T, number][],
  idOf: (x: T) => string | null | undefined,
  kind: string,
  err: Err,
  section: BundleKey,
  base?: string,
): Set<string> {
  const set = new Set<string>();
  for (const [x, i] of entries) {
    const v = idOf(x) ?? '';
    const index = base === undefined ? i : undefined;
    const path = base === undefined ? 'id' : `${base}.${i}.id`;
    if (v.trim() === '') err(section, index, path, `A ${kind} has no id.`);
    else if (set.has(v)) err(section, index, path, `Duplicate ${kind} id '${v}'.`);
    else set.add(v);
  }
  return set;
}

/** Ids of a list in the bundle (nulls skipped). */
export const idsIn = (list: ({ id?: string | null } | null | undefined)[] | null | undefined) =>
  new Set((list ?? []).map((x) => x?.id ?? '').filter((x) => x !== ''));

/** `value` with `defaults` for keys it doesn't have (undefined), the way the server's parser fills C# initializers. */
export function filled<T extends object>(defaults: T, value: Partial<T> | null | undefined): T {
  const out = { ...defaults } as Record<string, unknown>;
  for (const [k, v] of Object.entries(value ?? {})) if (v !== undefined) out[k] = v;
  return out as T;
}
